import i18n from '../i18n/config';

// Locale mapping for Intl APIs — the single resolution rule for the whole app,
// used by getCurrentLocale below and re-exported from branding.ts for the
// settlement page, so the same language renders in the same locale everywhere
// (DEFECT-30). A tag matches on its primary subtag, case-insensitively ('de',
// 'de-DE' and 'DE-at' are all German); anything else falls back to en-US.
const localeMap: Record<string, string> = {
  en: 'en-US',
  de: 'de-DE',
};

export const resolveLocale = (language: string | undefined): string =>
  localeMap[(language || '').toLowerCase().split('-')[0]] || localeMap.en;

// Get the current locale for Intl APIs
export const getCurrentLocale = (): string => resolveLocale(i18n.language);

// Date formatting options
type DateFormatStyle = 'short' | 'long' | 'dateTime' | 'time';

const dateFormatOptions: Record<DateFormatStyle, Intl.DateTimeFormatOptions> = {
  short: {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  },
  long: {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  },
  dateTime: {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  },
  time: {
    hour: '2-digit',
    minute: '2-digit',
  },
};

// Format a date according to the current locale
export const formatDate = (
  date: Date | string | number | null | undefined,
  style: DateFormatStyle = 'short'
): string => {
  if (!date) return '-';

  const dateObj = date instanceof Date ? date : new Date(date);

  if (isNaN(dateObj.getTime())) return '-';

  return new Intl.DateTimeFormat(getCurrentLocale(), dateFormatOptions[style]).format(dateObj);
};

// Format a number according to the current locale
export const formatNumber = (
  value: number | null | undefined,
  options?: Intl.NumberFormatOptions
): string => {
  if (value === null || value === undefined) return '-';

  return new Intl.NumberFormat(getCurrentLocale(), options).format(value);
};

// Format currency according to the current locale
export const formatCurrency = (
  value: number | null | undefined,
  currency: string = 'EUR'
): string => {
  if (value === null || value === undefined) return '-';

  return new Intl.NumberFormat(getCurrentLocale(), {
    style: 'currency',
    currency,
  }).format(value);
};

// Format cents as currency (divide by 100)
export const formatCentsAsCurrency = (
  cents: number | null | undefined,
  currency: string = 'EUR'
): string => {
  if (cents === null || cents === undefined) return '-';

  return formatCurrency(cents / 100, currency);
};

// Format percentage according to the current locale
export const formatPercent = (
  value: number | null | undefined,
  decimals: number = 2
): string => {
  if (value === null || value === undefined) return '-';

  return new Intl.NumberFormat(getCurrentLocale(), {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value / 100);
};

// Format a decimal percentage (e.g., 19.00 -> "19.00%")
export const formatPercentValue = (
  value: number | null | undefined,
  decimals: number = 2
): string => {
  if (value === null || value === undefined) return '-';

  return `${formatNumber(value, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}%`;
};

// Format relative time (e.g., "5 minutes ago")
export const formatRelativeTime = (
  date: Date | string | number | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string
): string => {
  if (!date) return t('common.never');

  const dateObj = date instanceof Date ? date : new Date(date);

  if (isNaN(dateObj.getTime())) return '-';

  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) {
    return t('apiKeys.justNow');
  } else if (diffMinutes < 60) {
    return t('apiKeys.minAgo', { count: diffMinutes });
  } else if (diffHours === 1) {
    return t('apiKeys.hourAgo', { count: 1 });
  } else if (diffHours < 24) {
    return t('apiKeys.hoursAgo', { count: diffHours });
  } else if (diffDays === 1) {
    return t('apiKeys.dayAgo', { count: 1 });
  } else {
    return t('apiKeys.daysAgo', { count: diffDays });
  }
};

// Hook for using formatters with automatic re-render on language change
export const useFormatters = () => {
  // Force re-render when language changes by depending on i18n.language
  const locale = i18n.language;

  return {
    formatDate: (date: Date | string | number | null | undefined, style?: DateFormatStyle) =>
      formatDate(date, style),
    formatNumber,
    formatCurrency,
    formatCentsAsCurrency,
    formatPercent,
    formatPercentValue,
    locale,
  };
};
